import * as React from 'react';
import type { DashboardData } from 'features/dashboard/types/dashboard';

export type UseDashboardDataResult = {
  data: DashboardData;
  isLoading: boolean;
  error: string | null;
};

type UseDashboardDataOptions = {
  enabled?: boolean;
};

const defaultDashboardData: DashboardData = {
  client: {
    id: '',
    name: '',
    typeLabel: '',
    primaryContactName: '',
    addressLine1: '',
    addressLine2: '',
    phone: '',
    email: ''
  },
  certificates: [],
  requests: [],
  linkedPolicies: [],
  additionalInterests: [],
  activity: [],
  agency: {
    id: '',
    name: '',
    typeLabel: ''
  },
  agent: {
    id: '',
    name: '',
    licenseNumber: '',
    phone: '',
    email: ''
  }
};

export function useDashboardData(options?: UseDashboardDataOptions): UseDashboardDataResult {
  const enabled = options?.enabled ?? true;
  const [data, setData] = React.useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(enabled);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      try {
        setData(defaultDashboardData);
        setIsLoading(false);
      } catch (e) {
        setError('Failed to load dashboard');
        setIsLoading(false);
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [enabled]);

  return {
    data: data ?? defaultDashboardData,
    isLoading,
    error
  };
}
